import { API_BASE_URL } from '../constants';

export class AuthService {
  constructor() {
    this.user = null;
    this.token = null;
    this.isAuthenticated = false;
    this.listeners = new Set();
  }

  // Event system for auth state changes
  addAuthStateListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback); // Return cleanup function
  }

  notifyAuthStateChange() {
    this.listeners.forEach(callback => {
      try {
        callback(this.isAuthenticated, this.user);
      } catch (error) {
        console.error('Auth state listener error:', error);
      }
    });
  }

  async googleSignIn(credential) {
    try {
      // Send the Google credential to your backend for verification
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential }),
      });

      if (!response.ok) {
        throw new Error('Failed to authenticate with server');
      }

      const data = await response.json();

      // Store authentication data
      this.user = data.user;
      this.token = data.token;
      this.isAuthenticated = true;

      // Store in localStorage for persistence
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Notify listeners of auth state change
      this.notifyAuthStateChange();

      return data;
    } catch (error) {
      console.error('Google sign-in failed:', error);
      throw error;
    }
  }

  async signOut() {
    try {
      // Call backend logout if needed
      if (this.token) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state
      this.user = null;
      this.token = null;
      this.isAuthenticated = false;

      // Clear localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');

      // Notify listeners of auth state change
      this.notifyAuthStateChange();
    }
  }

  restoreSession() {
    try {
      const token = localStorage.getItem('authToken');
      const userStr = localStorage.getItem('user');

      if (token && userStr) {
        this.token = token;
        this.user = JSON.parse(userStr);
        this.isAuthenticated = true;
        return true;
      }
    } catch (error) {
      console.error('Failed to restore session:', error);
      this.clearSession();
    }
    return false;
  }

  clearSession() {
    this.user = null;
    this.token = null;
    this.isAuthenticated = false;
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');

    // Notify listeners of auth state change
    this.notifyAuthStateChange();
  }

  getUser() {
    return this.user;
  }

  getToken() {
    return this.token;
  }

  isUserAuthenticated() {
    return this.isAuthenticated;
  }

  // Verify if the current token is still valid
  async verifyToken() {
    if (!this.token) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: this.token }),
      });

      if (response.ok) {
        return true;
      } else {
        // Token is invalid or expired
        this.clearSession();
        return false;
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      return false;
    }
  }

  // Wrapper around fetch that redirects to login on 401 (expired/invalid token)
  async apiFetch(url, options = {}) {
    const response = await fetch(url, options);
    if (response.status === 401) {
      this.clearSession();
    }
    return response;
  }

  // Add authorization header to fetch requests
  getAuthHeaders() {
    if (this.token) {
      return {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      };
    }
    return {
      'Content-Type': 'application/json',
    };
  }
}

// Create singleton instance
export const authService = new AuthService();